import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { Input } from "@/components/lilia-ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { Switch } from "@/components/lilia-ui/switch";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/utils/hooks/useTranslation";
import type { DocumentLook } from "@/lib/api";
import { normaliseLook, type Chapter } from "@/lib/themes/look";
import { useThemes } from "@/hooks/useThemes";
import { LookSection } from "./look/LookSection";

interface DocumentSettings {
  columns: number;
  columnGap: number;
  columnSeparator: string;
  paperSize: string;
  /** "portrait" (default) or "landscape" — drives the `landscape`
   *  documentclass option on the API render. */
  orientation?: "portrait" | "landscape";
  fontFamily: string;
  fontSize: number;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  headerText?: string;
  footerText?: string;
  // L/C/R header/footer slots — when any is set, the API renders all
  // populated slots via fancyhdr (\lhead/\chead/\rhead, etc.). The
  // legacy headerText/footerText above still work as fallbacks.
  headerLeft?: string;
  headerCenter?: string;
  headerRight?: string;
  footerLeft?: string;
  footerCenter?: string;
  footerRight?: string;
  lineSpacing?: number;
  paragraphIndent?: string;
  pageNumbering?: string;
  /** Numbered heading levels (null = class default, 0 = none, 1–5). Saved as -1 for the default. */
  headingNumbering?: number | null;
  experimentalLatexEdit?: boolean;
  /** Author-defined LaTeX macros / environments emitted into the export
   *  preamble (\newcommand, \DeclareMathOperator, \newenvironment, …). */
  customPreamble?: string;
  /** The document theme (null or absent = Classic). */
  look?: DocumentLook | null;
  /** Why themes are off for this document (a publisher class), or null. */
  lookLocked?: string | null;
}

interface DocumentSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  /** `look` is present only when it changed. */
  /** headingNumbering is sent as -1 for the class default (the API's reset), never null. */
  onSave: (settings: Partial<Omit<DocumentSettings, "look" | "lookLocked" | "headingNumbering">> & { look?: DocumentLook; headingNumbering?: number }) => void;
  settings: DocumentSettings;
  /** The document's chapters (numbered top-level headings), for Look's pins. */
  chapters?: readonly Chapter[];
}

const NO_CHAPTERS: readonly Chapter[] = [];

/** The look to send, or undefined when it did not change. Pins for headings
 *  that are no longer chapters are dropped on the way out. */
function lookToSave(
  initial: DocumentLook,
  current: DocumentLook,
  chapters: readonly Chapter[],
  locked: boolean,
): DocumentLook | undefined {
  if (locked) return undefined;
  const ids = new Set(chapters.map((c) => c.id));
  const pins = Object.fromEntries(Object.entries(current.pins).filter(([id]) => ids.has(id)));
  const next: DocumentLook = { theme: current.theme, paper: current.paper, pins, tables: current.tables };
  return JSON.stringify(next) === JSON.stringify(initial) ? undefined : next;
}

const PAPER_SIZES = [
  { value: "a4", label: "A4 (210 x 297 mm)" },
  { value: "letter", label: "US Letter (8.5 x 11 in)" },
  { value: "legal", label: "US Legal (8.5 x 14 in)" },
  { value: "a5", label: "A5 (148 x 210 mm)" },
];

const FONT_FAMILIES = [
  { value: "serif", label: "Serif (Times-like)" },
  { value: "sans-serif", label: "Sans Serif (Helvetica-like)" },
  { value: "monospace", label: "Monospace (Courier-like)" },
];

const COLUMN_OPTIONS = [
  { value: 1, label: "1 Column" },
  { value: 2, label: "2 Columns" },
  { value: 3, label: "3 Columns" },
];

const LINE_SPACING_OPTIONS = [
  { value: "1", label: "Single (1.0)" },
  { value: "1.15", label: "1.15" },
  { value: "1.5", label: "1.5" },
  { value: "2", label: "Double (2.0)" },
];

const PAGE_NUMBERING_OPTIONS = [
  { value: "arabic", label: "Arabic (1, 2, 3...)" },
  { value: "roman", label: "Roman (i, ii, iii...)" },
  { value: "none", label: "None" },
];

export function DocumentSettingsDialog({
  open,
  onClose,
  onSave,
  settings,
  chapters = NO_CHAPTERS,
}: DocumentSettingsDialogProps) {
  const { t } = useTranslation('dialogs');
  const { themes, isLoading: themesLoading, error: themesError } = useThemes({ enabled: open });
  const initialLook = useMemo(() => normaliseLook(settings.look), [settings.look]);
  const [look, setLook] = useState<DocumentLook>(initialLook);
  const [columns, setColumns] = useState(settings.columns);
  const [columnGap, setColumnGap] = useState(settings.columnGap);
  const [columnSeparator, setColumnSeparator] = useState(settings.columnSeparator === "rule");
  const [paperSize, setPaperSize] = useState(settings.paperSize);
  const [orientation, setOrientation] = useState<"portrait" | "landscape">(settings.orientation || "portrait");
  const [fontFamily, setFontFamily] = useState(settings.fontFamily);
  const [fontSize, setFontSize] = useState(settings.fontSize);
  const [marginTop, setMarginTop] = useState(settings.marginTop || "");
  const [marginBottom, setMarginBottom] = useState(settings.marginBottom || "");
  const [marginLeft, setMarginLeft] = useState(settings.marginLeft || "");
  const [marginRight, setMarginRight] = useState(settings.marginRight || "");
  const [headerText, setHeaderText] = useState(settings.headerText || "");
  const [footerText, setFooterText] = useState(settings.footerText || "");
  const [headerLeft, setHeaderLeft] = useState(settings.headerLeft || "");
  const [headerCenter, setHeaderCenter] = useState(settings.headerCenter || "");
  const [headerRight, setHeaderRight] = useState(settings.headerRight || "");
  const [footerLeft, setFooterLeft] = useState(settings.footerLeft || "");
  const [footerCenter, setFooterCenter] = useState(settings.footerCenter || "");
  const [footerRight, setFooterRight] = useState(settings.footerRight || "");
  const [lineSpacing, setLineSpacing] = useState(settings.lineSpacing?.toString() || "1");
  const [paragraphIndent, setParagraphIndent] = useState(settings.paragraphIndent || "");
  const [pageNumbering, setPageNumbering] = useState(settings.pageNumbering || "arabic");
  const headingNumberingValue = (n: number | null | undefined) => (n == null || n < 0 ? "default" : String(n));
  const [headingNumbering, setHeadingNumbering] = useState(headingNumberingValue(settings.headingNumbering));
  const [experimentalLatexEdit, setExperimentalLatexEdit] = useState(!!settings.experimentalLatexEdit);
  const [customPreamble, setCustomPreamble] = useState(settings.customPreamble || "");

  useEffect(() => {
    if (open) {
      setColumns(settings.columns);
      setColumnGap(settings.columnGap);
      setColumnSeparator(settings.columnSeparator === "rule");
      setPaperSize(settings.paperSize);
      setOrientation(settings.orientation || "portrait");
      setFontFamily(settings.fontFamily);
      setFontSize(settings.fontSize);
      setMarginTop(settings.marginTop || "");
      setMarginBottom(settings.marginBottom || "");
      setMarginLeft(settings.marginLeft || "");
      setMarginRight(settings.marginRight || "");
      setHeaderText(settings.headerText || "");
      setFooterText(settings.footerText || "");
      setHeaderLeft(settings.headerLeft || "");
      setHeaderCenter(settings.headerCenter || "");
      setHeaderRight(settings.headerRight || "");
      setFooterLeft(settings.footerLeft || "");
      setFooterCenter(settings.footerCenter || "");
      setFooterRight(settings.footerRight || "");
      setLineSpacing(settings.lineSpacing?.toString() || "1");
      setParagraphIndent(settings.paragraphIndent || "");
      setPageNumbering(settings.pageNumbering || "arabic");
      setHeadingNumbering(headingNumberingValue(settings.headingNumbering));
      setExperimentalLatexEdit(!!settings.experimentalLatexEdit);
      setCustomPreamble(settings.customPreamble || "");
    }
  }, [open, settings]);

  // The look resets only when the dialog opens: `settings` is rebuilt on every
  // render of the page, and a picked theme must survive an autosave tick.
  const lookOnOpen = settings.look;
  useEffect(() => {
    if (open) setLook(normaliseLook(lookOnOpen));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = () => {
    onSave({
      columns,
      columnGap,
      columnSeparator: columnSeparator ? "rule" : "none",
      paperSize,
      orientation,
      fontFamily,
      fontSize,
      marginTop: marginTop || undefined,
      marginBottom: marginBottom || undefined,
      marginLeft: marginLeft || undefined,
      marginRight: marginRight || undefined,
      headerText: headerText || undefined,
      footerText: footerText || undefined,
      headerLeft: headerLeft || undefined,
      headerCenter: headerCenter || undefined,
      headerRight: headerRight || undefined,
      footerLeft: footerLeft || undefined,
      footerCenter: footerCenter || undefined,
      footerRight: footerRight || undefined,
      lineSpacing: lineSpacing ? parseFloat(lineSpacing) : undefined,
      paragraphIndent: paragraphIndent || undefined,
      pageNumbering: pageNumbering || undefined,
      // Sent only when changed, so saving another setting never moves it.
      headingNumbering: headingNumbering === headingNumberingValue(settings.headingNumbering)
        ? undefined
        : headingNumbering === "default" ? -1 : Number(headingNumbering),
      experimentalLatexEdit,
      customPreamble,
      look: lookToSave(initialLook, look, chapters, !!settings.lookLocked),
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="document-settings-dialog" className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('settings.title')}</DialogTitle>
        </DialogHeader>

        <div className="py-2 max-h-[65vh] overflow-y-auto thin-scrollbar pr-1">
          {/* Look — the document theme (design-handoffs/2026-10-05-document-themes, 1e) */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">Look</h4>
            <LookSection
              value={look}
              onChange={setLook}
              themes={themes}
              themesLoading={themesLoading}
              themesError={!!themesError}
              lookLocked={settings.lookLocked ?? null}
              chapters={chapters}
            />
          </div>

          {/* Column Layout Section */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">{t('settings.columnLayout')}</h4>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.columns')}</label>
              <div className="flex gap-1.5">
                {COLUMN_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid="settings-columns"
                    data-value={opt.value}
                    aria-pressed={columns === opt.value}
                    className={cn(
                      "flex-1 py-2 px-3 text-xs font-medium text-[var(--lilia-text-muted)] bg-[var(--lilia-surface-alt,#f5f5f5)] border border-[var(--lilia-border)] rounded-md cursor-pointer transition-all duration-150",
                      "hover:bg-[var(--lilia-surface-hover,#ebebeb)] hover:border-[var(--lilia-primary)]",
                      columns === opt.value && "bg-[var(--lilia-primary)] text-[color:var(--lilia-on-primary)] border-[var(--lilia-primary)]"
                    )}
                    onClick={() => setColumns(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {columns > 1 && (
              <>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.columnGap')}</label>
                  <Input
                    type="number"
                    value={columnGap}
                    data-testid="settings-column-gap"
                    onChange={(e) => setColumnGap(Number(e.target.value) || 1.5)}
                    min={0.5}
                    max={5}
                    step={0.1}
                    className="w-[120px]"
                  />
                </div>

                <div className="mb-3 flex items-center gap-3">
                  <label className="text-xs font-semibold text-[var(--lilia-text)]">{t('settings.columnSeparatorLine')}</label>
                  <Switch
                    data-testid="settings-column-separator"
                    checked={columnSeparator}
                    onCheckedChange={setColumnSeparator}
                  />
                </div>
              </>
            )}
          </div>

          {/* Page Settings */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">{t('settings.page')}</h4>

            <div className="flex gap-4">
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.paperSize')}</label>
                <Select value={paperSize} onValueChange={(val) => setPaperSize(val)}>
                  <SelectTrigger className="w-full" data-testid="settings-paper-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAPER_SIZES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mb-3 w-40">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">Orientation</label>
                <Select value={orientation} onValueChange={(val) => setOrientation(val as "portrait" | "landscape")}>
                  <SelectTrigger className="w-full" data-testid="settings-orientation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Typography Settings */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">{t('settings.typography')}</h4>

            <div className="flex gap-4">
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.fontFamily')}</label>
                <Select value={fontFamily} onValueChange={(val) => setFontFamily(val)}>
                  <SelectTrigger className="w-full" data-testid="settings-font-family">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_FAMILIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mb-3 max-w-[120px]">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.fontSize')}</label>
                <Input
                  type="number"
                  value={fontSize}
                  data-testid="settings-font-size"
                  onChange={(e) => setFontSize(Number(e.target.value) || 12)}
                  min={8}
                  max={24}
                  step={1}
                  className="w-[120px]"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.lineSpacing')}</label>
                <Select value={lineSpacing} onValueChange={(val) => setLineSpacing(val)}>
                  <SelectTrigger className="w-full" data-testid="settings-line-spacing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINE_SPACING_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.paragraphIndent')}</label>
                <Input
                  value={paragraphIndent}
                  data-testid="settings-paragraph-indent"
                  onChange={(e) => setParagraphIndent(e.currentTarget.value)}
                  placeholder="e.g. 1.5em"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Margins */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">{t('settings.margins')}</h4>
            <div className="flex gap-4">
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.top')}</label>
                <Input data-testid="settings-margin-top" value={marginTop} onChange={(e) => setMarginTop(e.currentTarget.value)} placeholder="1in" />
              </div>
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.bottom')}</label>
                <Input data-testid="settings-margin-bottom" value={marginBottom} onChange={(e) => setMarginBottom(e.currentTarget.value)} placeholder="1in" />
              </div>
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.left')}</label>
                <Input data-testid="settings-margin-left" value={marginLeft} onChange={(e) => setMarginLeft(e.currentTarget.value)} placeholder="1in" />
              </div>
              <div className="mb-3 flex-1">
                <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.right')}</label>
                <Input data-testid="settings-margin-right" value={marginRight} onChange={(e) => setMarginRight(e.currentTarget.value)} placeholder="1in" />
              </div>
            </div>
          </div>

          {/* Headers, Footers & Page Numbering */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">{t('settings.headersAndFooters')}</h4>
            {/* Three-slot header/footer per fancyhdr — Left / Center /
                Right. Set whatever combination you need; empty slots
                are skipped. The legacy headerText/footerText below
                stay as a single-line shortcut (header → \lhead,
                footer → \rfoot) when none of the L/C/R slots is set. */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1.5">Header (left · center · right)</label>
              <div className="flex gap-2">
                <Input data-testid="settings-header-left" value={headerLeft} onChange={(e) => setHeaderLeft(e.currentTarget.value)} placeholder="Left" />
                <Input data-testid="settings-header-center" value={headerCenter} onChange={(e) => setHeaderCenter(e.currentTarget.value)} placeholder="Center" />
                <Input data-testid="settings-header-right" value={headerRight} onChange={(e) => setHeaderRight(e.currentTarget.value)} placeholder="Right" />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1.5">Footer (left · center · right)</label>
              <div className="flex gap-2">
                <Input data-testid="settings-footer-left" value={footerLeft} onChange={(e) => setFooterLeft(e.currentTarget.value)} placeholder="Left" />
                <Input data-testid="settings-footer-center" value={footerCenter} onChange={(e) => setFooterCenter(e.currentTarget.value)} placeholder="Center" />
                <Input data-testid="settings-footer-right" value={footerRight} onChange={(e) => setFooterRight(e.currentTarget.value)} placeholder="Right" />
              </div>
            </div>

            {/* Legacy single-slot fields kept as a fallback. The API
                renders these only when no L/C/R slot above is set. */}
            <details className="mb-3">
              <summary className="text-xs font-semibold text-[var(--lilia-text-muted)] cursor-pointer mb-1">{t('settings.headerText')} / {t('settings.footerText')} (legacy single-slot)</summary>
              <div className="mt-2 space-y-2">
                <Input value={headerText} onChange={(e) => setHeaderText(e.currentTarget.value)} placeholder="Header (single slot, → \lhead)" />
                <Input value={footerText} onChange={(e) => setFooterText(e.currentTarget.value)} placeholder="Footer (single slot, → \rfoot)" />
              </div>
            </details>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.pageNumbering')}</label>
              <Select value={pageNumbering} onValueChange={(val) => setPageNumbering(val)}>
                <SelectTrigger className="w-full" data-testid="settings-page-numbering">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_NUMBERING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Which heading levels carry a number; the table of contents lists the same levels. */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[var(--lilia-text)] mb-1">{t('settings.headingNumbering')}</label>
              <Select value={headingNumbering} onValueChange={(val) => setHeadingNumbering(val)}>
                <SelectTrigger className="w-full" data-testid="settings-heading-numbering">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">{t('settings.headingNumberingDefault')}</SelectItem>
                  <SelectItem value="0">{t('settings.headingNumberingNone')}</SelectItem>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={String(n)}>{t('settings.headingNumberingLevels', { count: n })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-[var(--lilia-text-muted)]">{t('settings.headingNumberingHint')}</p>
            </div>
          </div>

          {/* Custom preamble — author-defined LaTeX macros / environments */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">Custom preamble</h4>
            <p className="text-[11px] text-[var(--lilia-text-muted)] leading-relaxed mb-2">
              LaTeX macros and environments added to the document preamble — e.g.{" "}
              <code className="font-mono">{"\\newcommand{\\R}{\\mathbb{R}}"}</code> or{" "}
              <code className="font-mono">{"\\DeclareMathOperator{\\argmin}{argmin}"}</code>. Used throughout
              the document and emitted into the LaTeX export. You own its correctness.
            </p>
            <textarea
              data-testid="document-custom-preamble"
              className="w-full h-32 px-2.5 py-2 text-[12.5px] font-mono rounded-md border border-[var(--lilia-border)] bg-[var(--lilia-bg)] text-[var(--lilia-text)] focus:outline-none focus:ring-1 focus:ring-[var(--lilia-primary)] resize-y"
              placeholder={"\\newcommand{\\R}{\\mathbb{R}}\n\\DeclareMathOperator{\\argmin}{argmin}"}
              spellCheck={false}
              value={customPreamble}
              onChange={(e) => setCustomPreamble(e.target.value)}
            />
          </div>

          {/* Experimental — opt-in features that may reshape content on save */}
          <div className="mb-6">
            <h4 className="m-0 mb-3 text-[13px] font-bold text-[var(--lilia-text)] uppercase tracking-wide pb-1.5 border-b border-[var(--lilia-border)]">Experimental</h4>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-[var(--lilia-text)] mb-1">Edit blocks as LaTeX</p>
                <p className="text-[11px] text-[var(--lilia-text-muted)] leading-relaxed">
                  Adds a LaTeX tab to every block for direct source editing. Rich blocks (tables, figures with nested content) may reshape on save — keep a backup copy of important documents.
                </p>
              </div>
              <Switch
                data-testid="settings-latex-edit"
                checked={experimentalLatexEdit}
                onCheckedChange={setExperimentalLatexEdit}
              />
            </div>
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="settings-cancel" onClick={onClose}>{t('common:cancel')}</Button>
          <Button variant="primary" data-testid="settings-save" onClick={handleSave}>
            {t('common:save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
